import { apiSlice } from "../../../features/api/apiSlice";

export type StateRef = { id: number; name: string };
export type SubCategory = { id: number; name: string; databank_category_id?: number };
export type Category = { id: number; name: string; sub_categories?: SubCategory[] };

export type DatabankItem = {
  id: number;
  state_id: number;
  state?: StateRef | null;
  description: string;
  unit?: string | null;
  category_id?: number | null;
  category?: { id: number; name: string } | null;
  sub_category_id?: number | null;
  sub_category?: { id: number; name: string } | null;
  month?: string | null;
  year: number;
  price: number | string;
};

export type DatabankFilters = {
  states: StateRef[];
  categories: Category[];
  months: string[];
  years: number[];
};

/** One row of a count + money breakdown (by state, category, unit, year…). */
export type AnalysisRow = {
  label: string;
  count: number;
  average: number;
  min: number;
  max: number;
};

export type DatabankAnalysis = {
  meta: { total: number; generated_at: string; filters: Record<string, any> };
  summary: {
    total_records: number;
    distinct_items: number;
    states_covered: number;
    states_total: number;
    categories_covered: number;
    sub_categories_covered: number;
    units_used: number;
    contributors: number;
    years_covered: number[];
    earliest_year: number | null;
    latest_year: number | null;
    last_updated_at: string | null;
  };
  price_stats: {
    count: number; min: number; max: number; average: number;
    median: number; p25: number; p75: number; total_value: number;
  };
  by_state: AnalysisRow[];
  by_category: AnalysisRow[];
  by_sub_category: AnalysisRow[];
  by_unit: AnalysisRow[];
  by_year: AnalysisRow[];
  by_month: AnalysisRow[];
  price_bands: Record<string, number>;
  most_expensive: ExtremeRow[];
  least_expensive: ExtremeRow[];
  most_tracked: Array<{ label: string; count: number; average: number; states: number }>;
  widest_spread: Array<{
    label: string; unit: string; states: number;
    min: number; max: number; average: number; spread: number; spread_pct: number;
  }>;
  contributors: Array<{ label: string; count: number }>;
  uncovered_states: string[];
};

export type ExtremeRow = {
  description: string;
  unit: string;
  price: number;
  state: string;
  category: string;
  sub_category: string;
  period: string;
};

type AnalysisQuery = {
  admin?: boolean;
  state_id?: string | number;
  category_id?: string | number;
  sub_category_id?: string | number;
  unit?: string;
  month?: string;
  year?: string | number;
  year_from?: string | number;
  year_to?: string | number;
  q?: string;
};

type ItemQuery = {
  admin?: boolean;
  state_id?: string | number;
  category_id?: string | number;
  sub_category_id?: string | number;
  month?: string;
  year?: string | number;
  q?: string;
  page?: number;
  per_page?: number;
};

// The API sometimes wraps collections (e.g. { data: [...] }); always resolve
// to a plain array so the UI never receives a non-iterable value.
function toArray(r: any): any[] {
  if (Array.isArray(r)) return r;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r?.data?.data)) return r.data.data;
  return [];
}

function buildQuery(args: ItemQuery) {
  const p = new URLSearchParams();
  (["state_id", "category_id", "sub_category_id", "month", "year", "q", "page", "per_page"] as const).forEach((k) => {
    const v = (args as any)[k];
    if (v !== undefined && v !== null && String(v) !== "") p.set(k, String(v));
  });
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

export const databankApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    getDatabankItems: b.query<any, ItemQuery>({
      query: (args) => `/${args.admin ? "admin/databank" : "databank"}/items${buildQuery(args)}`,
      providesTags: ["DatabankItems"],
    }),

    // Aggregate report over the SAME filtered set the browse list shows, plus
    // a year range. Every filter is optional; nothing set means the whole
    // register.
    getDatabankAnalysis: b.query<DatabankAnalysis, AnalysisQuery>({
      query: (args) => {
        const p = new URLSearchParams();
        (["state_id", "category_id", "sub_category_id", "unit", "month", "year", "year_from", "year_to", "q"] as const)
          .forEach((k) => {
            const v = (args as any)[k];
            if (v !== undefined && v !== null && String(v) !== "") p.set(k, String(v));
          });
        const qs = p.toString();
        return `/${args?.admin ? "admin/databank" : "databank"}/analysis${qs ? `?${qs}` : ""}`;
      },
      transformResponse: (r: any) => r?.data ?? r,
      providesTags: ["DatabankItems"],
    }),

    getDatabankFilters: b.query<DatabankFilters, { admin?: boolean } | void>({
      query: (args) => `/${args?.admin ? "admin/databank" : "databank"}/filters`,
      transformResponse: (r: any) => {
        const src = r?.data && !Array.isArray(r.data) ? r.data : r;
        return {
          states: toArray(src?.states),
          categories: toArray(src?.categories),
          months: toArray(src?.months),
          years: toArray(src?.years),
        };
      },
      providesTags: ["DatabankFilters"],
    }),

    createDatabankItem: b.mutation<any, Partial<DatabankItem>>({
      query: (body) => ({ url: "/admin/databank/items", method: "POST", body }),
      invalidatesTags: ["DatabankItems", "DatabankFilters"],
    }),

    updateDatabankItem: b.mutation<any, { id: number; body: Partial<DatabankItem> }>({
      query: ({ id, body }) => ({ url: `/admin/databank/items/${id}`, method: "PUT", body }),
      invalidatesTags: ["DatabankItems", "DatabankFilters"],
    }),

    deleteDatabankItem: b.mutation<any, number>({
      query: (id) => ({ url: `/admin/databank/items/${id}`, method: "DELETE" }),
      invalidatesTags: ["DatabankItems", "DatabankFilters"],
    }),

    importDatabank: b.mutation<any, FormData>({
      query: (body) => ({ url: "/admin/databank/import", method: "POST", body }),
      invalidatesTags: ["DatabankItems", "DatabankFilters", "DatabankCategories"],
    }),

    // ── Categories (admin-controlled) ────────────────────────────────────────
    getDatabankCategories: b.query<Category[], { admin?: boolean } | void>({
      query: (args) => `/${args?.admin ? "admin/databank" : "databank"}/categories`,
      transformResponse: (r: any) => toArray(r),
      providesTags: ["DatabankCategories"],
    }),

    createDatabankCategory: b.mutation<any, { name: string }>({
      query: (body) => ({ url: "/admin/databank/categories", method: "POST", body }),
      invalidatesTags: ["DatabankCategories", "DatabankFilters"],
    }),

    updateDatabankCategory: b.mutation<any, { id: number; name: string }>({
      query: ({ id, name }) => ({ url: `/admin/databank/categories/${id}`, method: "PUT", body: { name } }),
      invalidatesTags: ["DatabankCategories", "DatabankFilters"],
    }),

    deleteDatabankCategory: b.mutation<any, number>({
      query: (id) => ({ url: `/admin/databank/categories/${id}`, method: "DELETE" }),
      invalidatesTags: ["DatabankCategories", "DatabankFilters", "DatabankItems"],
    }),

    createDatabankSubCategory: b.mutation<any, { categoryId: number; name: string }>({
      query: ({ categoryId, name }) => ({ url: `/admin/databank/categories/${categoryId}/sub-categories`, method: "POST", body: { name } }),
      invalidatesTags: ["DatabankCategories", "DatabankFilters"],
    }),

    updateDatabankSubCategory: b.mutation<any, { id: number; name: string }>({
      query: ({ id, name }) => ({ url: `/admin/databank/sub-categories/${id}`, method: "PUT", body: { name } }),
      invalidatesTags: ["DatabankCategories", "DatabankFilters"],
    }),

    deleteDatabankSubCategory: b.mutation<any, number>({
      query: (id) => ({ url: `/admin/databank/sub-categories/${id}`, method: "DELETE" }),
      invalidatesTags: ["DatabankCategories", "DatabankFilters", "DatabankItems"],
    }),
  }),
});

export const {
  useGetDatabankItemsQuery,
  useGetDatabankAnalysisQuery,
  useGetDatabankFiltersQuery,
  useCreateDatabankItemMutation,
  useUpdateDatabankItemMutation,
  useDeleteDatabankItemMutation,
  useImportDatabankMutation,
  useGetDatabankCategoriesQuery,
  useCreateDatabankCategoryMutation,
  useUpdateDatabankCategoryMutation,
  useDeleteDatabankCategoryMutation,
  useCreateDatabankSubCategoryMutation,
  useUpdateDatabankSubCategoryMutation,
  useDeleteDatabankSubCategoryMutation,
} = databankApi;
